// Recognises the responses that mean "this candidate link can no longer be used" so
// the form can show one clear message instead of leaking a raw axios error to the
// candidate.
//
// A link stops working for a few reasons, and the backend signals each differently:
//   - superseded / deleted key (e.g. the client re-sent the invitation, which rotates
//     the token) -> mutations answer 404 "Not Found"; the GET form endpoint answers
//     400 "Invalid request"; both mean the same thing to the candidate: invalid.
//   - expired / canceled / already-submitted -> a specific "Pre Check is ..." message.
// The GET form endpoint reports those last three as a 200 with a `noFormMessage`
// instead of an error, so there are two entry points below.

// Candidate-form endpoints that authenticate with the emailed key. The interceptor
// only reacts to these, so an unrelated public call failing (an S3 upload, another
// public page) never flips the form into an unavailable state. The S3 upload PUT uses
// an absolute URL, so it does not appear here and is excluded automatically.
export const CANDIDATE_FORM_ENDPOINTS = new Set([
    "/pre-check-form",
    "/pre-check-form-update-id",
    "/save-pre-check-consent-progress",
    "/submit-pre-check-consent",
    "/submit-pre-check-form",
    "/start-pre-check-session",
    "/skip-pre-check",
    "/pre-check-do-not-agree",
    "/pre-check-additional-document-media-url",
    // Page-progress tracking answers 400 "Pre Check is expired" for an expired link, so a form left
    // open past expiry flips to the expired screen (its other failures always return 200).
    "/track-pre-check-form-progress",
])

// The two GET endpoints where a missing/rotated key is the only realistic cause of a
// 400 "Invalid request". On the POST mutations a bare "Invalid request" is more likely
// a request-validation problem, so it is not treated as a dead link there.
const GET_KEY_ENDPOINTS = new Set([
    "/pre-check-form",
    "/pre-check-form-update-id",
])

const pathOf = (error) => (error?.config?.url || "").split("?")[0]

// Maps a failed candidate-endpoint request to a reason, or null when the failure is
// something else (a transient 500, a network drop, a plain validation error) that
// should not be presented as an invalid link.
export const classifyFormError = (error) => {

    const path = pathOf(error)

    if(!CANDIDATE_FORM_ENDPOINTS.has(path)) {
        return null
    }

    const status = error?.response?.status
    const serverError = error?.response?.data?.error

    // No form matched the key: re-sent (rotated), canceled-then-deleted, or never valid.
    if(status === 404) {
        return "invalid"
    }

    if(status === 400) {
        if(serverError === "Pre Check is expired") return "expired"
        if(serverError === "Pre Check is canceled") return "canceled"
        if(serverError === "Pre Check is completed") return "completed"
        if(serverError === "Invalid request" && GET_KEY_ENDPOINTS.has(path)) return "invalid"
    }

    return null
}

// The GET form endpoint reports a spent/expired/canceled link as a successful response
// carrying `noFormMessage`, so it never reaches classifyFormError. Map that shape here.
// Note the backend's historical "cenceled" spelling of the canceled status.
export const classifyNoFormMessage = (data) => {

    if(!data?.noFormMessage) {
        return null
    }

    if(data.status === "expired") return "expired"
    if(data.status === "no_longer_required") return "no_longer_required"
    if(data.status === "completed") return "completed"
    if(data.status === "cenceled" || data.status === "canceled") return "canceled"

    return "invalid"
}
